import React, { useEffect, useState } from "react";
import { useAudioRecorder } from "react-audio-voice-recorder";
import { FaPlay, FaPause, FaStop, FaRedo, FaMicrophone } from "react-icons/fa";
import { IoSendSharp } from "react-icons/io5";

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "10px",
    border: "1px solid #ccc",
    borderRadius: "8px",
    padding: "16px",
    backgroundColor: "#f9f9f9",
  },
  status: {
    fontSize: "14px",
    fontWeight: "bold",
    color: "#333",
  },
  controls: {
    display: "flex",
    gap: "8px",
  },
  btn: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    backgroundColor: "#007bff",
    color: "white",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background-color 0.2s ease, transform 0.1s ease",
  },
  sendButton: {
    alignItems: "right",
    justifyContent: "center",
  },
  disabledBtn: {
    backgroundColor: "#66b2ff",
    color: "#e6f0fa", 
    cursor: "not-allowed",
  }
};

const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
};

const AudioRecorder = ({ onRecordingReady, extraData }) => {

  const isMediaSupported =
    typeof navigator !== "undefined" &&
    navigator.mediaDevices &&
    typeof navigator.mediaDevices.getUserMedia === "function";

    console.log("Navigator: "+ navigator.mediaDevices);

  // const isMediaSupported = true;
  
  const {
    startRecording,
    stopRecording,
    togglePauseResume,
    recordingBlob,
    isRecording,
    isPaused,
    recordingTime
  } = useAudioRecorder(
    isMediaSupported ? {
          audioTrackConstraints: {
            noiseSuppression: true,
            echoCancellation: true
          },
          onNotAllowedOrFound: (err) => {
            console.error("Mic access error:", err);
            alert(
              "Unable to access microphone. Please check browser permissions and retry."
            );
          }
        } : undefined
    );

  if (!isMediaSupported) {
    return (
      <div style={styles.container}>
      <div style={{ padding: "1rem", color: "red"}}>
        Microphone access is not supported in this environment.
        <br />
        Please open the app on <code>https://</code> or <code>localhost</code>{" "}
        in a supported browser.
      </div>
      </div>
    );
  }
  else {

  const [audioUrl, setAudioUrl] = useState(null);
  const [stopped, setStopped] = useState(false);

  // Generate & cleanup audio URL
  useEffect(() => {
    if (recordingBlob) {
      const url = URL.createObjectURL(recordingBlob);
      setAudioUrl(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [recordingBlob]);

  const handleStop = () => {
    stopRecording();
    setStopped(true); // Disable play button after first stop
  };

  const handleReRecord = () => {
    setAudioUrl(null);
    setStopped(false); // allow new recording
    startRecording();
  };

  const handleSendAudio = async () => {
    try {
      await onRecordingReady({
        blob: recordingBlob,
        ...extraData,
      });

      // Reset controls
      setAudioUrl(null);
      setStopped(false);
    } catch (err) {
      console.error("Failed to send audio: ", err);
    }
  }

  return (
    <div style={styles.container}>
      {/* 1. Audio Preview (only after recording) */}
      {audioUrl && (
        <audio controls src={audioUrl} style={{ width: "100%" }} />
      )}

      {/* 2. Status / Timer (only while recording or paused) */}
      {isRecording && (
        <div style={styles.status}>
          {isPaused ? "Paused" : `${formatTime(recordingTime)}s`}
        </div>
      )}

      {/* 3. Controls */}
      <div style={styles.controls}>
        {/* Play (disabled after stop) */}
        {!isRecording && (
          <button
            style={{
              ...styles.btn,
              ...(stopped ? styles.disabledBtn : {}),
            }}
            onClick={startRecording}
            disabled={stopped}
          >
            <FaMicrophone size={14} title="Start Recording" aria-label="Start Recording" />
          </button>
        )}

        {/* Pause / Resume (only while recording) */}
        {isRecording && (
          <button style={styles.btn} onClick={togglePauseResume}>
            {isPaused ? (
              <FaPlay size={18} title="Resume Recording" aria-label="Resume Recording" />
            ) : (
              <FaPause size={18} title="Pause Recording" aria-label="Pause Recording" />
            )}
          </button>
        )}

        {/* Stop (only while recording) */}
        {isRecording && (
          <button style={styles.btn} onClick={handleStop}>
            <FaStop size={18} title="Stop Recording" aria-label="Stop Recording" />
          </button>
        )}

        {/* Re-record (enabled only when audio exists) */}
        <button
          style={{
            ...styles.btn,
            ...(audioUrl ? {} : styles.disabledBtn),
          }}
          onClick={handleReRecord}
          disabled={!audioUrl}
        >
          <FaRedo size={18} title="Re-record" aria-label="Re-record" />
        </button>

        {/* Send (enabled only when audio exists) */}
        <button
          style={{
            ...styles.btn,
            ...(audioUrl ? {} : styles.disabledBtn),
          }}
          onClick={
            // () =>
            // onRecordingReady({
            //   blob: recordingBlob,
            //   ...extraData,
            // })
            handleSendAudio
          }
          disabled={!audioUrl}
        >
          <IoSendSharp className="sendButton" size={18} title="Send" aria-label="Send" />
        </button>
      </div>
    </div>
  );
  }
};

export default AudioRecorder;
